import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { ChevronRight, FileText } from 'lucide-react';

interface TOCItem {
  id: string;
  title: string;
  level: number;
}

interface ReportTableOfContentsProps {
  items: TOCItem[];
  className?: string;
}

const ReportTableOfContents = ({ items, className }: ReportTableOfContentsProps) => {
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-100px 0px -80% 0px',
        threshold: 0,
      }
    );

    items.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [items]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <nav className={cn('space-y-1', className)}>
      <div className="flex items-center gap-2 mb-4 pb-3 border-b">
        <FileText className="h-4 w-4 text-fordje-blue" />
        <span className="font-semibold text-sm text-fordje-navy">Contents</span>
      </div>
      {items.map((item) => (
        <button
          key={item.id}
          onClick={() => scrollToSection(item.id)}
          className={cn(
            'block w-full text-left text-sm py-1.5 px-2 rounded transition-colors hover:bg-fordje-blue/5',
            item.level === 1 && 'font-medium',
            item.level === 2 && 'pl-4 text-muted-foreground',
            item.level === 3 && 'pl-6 text-muted-foreground text-xs',
            activeId === item.id && 'bg-fordje-blue/10 text-fordje-blue border-l-2 border-fordje-blue'
          )}
        >
          <span className="flex items-center gap-1">
            {activeId === item.id && <ChevronRight className="h-3 w-3" />}
            {item.title}
          </span>
        </button>
      ))}
    </nav>
  );
};

export default ReportTableOfContents;
